import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, Icon, useToast } from '../../components/ui';

// The marketing agent (EventBridge rule, Facebook token secret, tables) lives in us-east-1,
// separate from the ops dashboard's own region.
const REGION = 'us-east-1';
const ENV = process.env.REACT_APP_ENVIRONMENT || 'prod';

const CONSOLE_LINKS = [
  { label: 'Facebook token (Secrets Manager)', sub: `hca-facebook-token-${ENV}`, url: `https://${REGION}.console.aws.amazon.com/secretsmanager/secret?name=hca-facebook-token-${ENV}&region=${REGION}` },
  { label: 'Daily post rule (EventBridge)', sub: `hca-daily-post-${ENV}`, url: `https://${REGION}.console.aws.amazon.com/events/home?region=${REGION}#/rules` },
  { label: 'Lambda functions', sub: 'Agent and admin API code', url: `https://${REGION}.console.aws.amazon.com/lambda/home?region=${REGION}#/functions` },
  { label: 'DynamoDB tables', sub: 'Posts, leads, comments, audit log', url: `https://${REGION}.console.aws.amazon.com/dynamodbv2/home?region=${REGION}#tables` },
  { label: 'CloudWatch logs', sub: 'Agent run output', url: `https://${REGION}.console.aws.amazon.com/cloudwatch/home?region=${REGION}#logsV2:log-groups` },
  { label: 'Escalation topic (SNS)', sub: 'Email alerts for sensitive messages', url: `https://${REGION}.console.aws.amazon.com/sns/v3/home?region=${REGION}#/topics` },
];

function PostSchedulePanel({ showToast }) {
  const [schedule, setSchedule] = useState(null);
  const [ruleState, setRuleState] = useState(null);
  const [selected, setSelected] = useState('');
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function load() {
    setError('');
    api.getPostSchedule()
      .then(({ schedule: s, options: o, state: st }) => {
        setSchedule(s); setSelected(s); setOptions(o); setRuleState(st);
      })
      .catch(e => setError(e.message || 'Unable to load the posting schedule'));
  }

  useEffect(() => { load(); }, []); // intentionally run once on mount

  async function handleSave() {
    if (!selected || selected === schedule) return;
    setSaving(true);
    try {
      const { label } = await api.updatePostSchedule(selected);
      setSchedule(selected);
      showToast(`Post frequency updated: ${label}`);
    } catch (e) {
      showToast(`Could not update the frequency: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleTogglePause() {
    const newState = ruleState === 'ENABLED' ? 'DISABLED' : 'ENABLED';
    setSaving(true);
    try {
      await api.setPostScheduleState(newState);
      setRuleState(newState);
      showToast(newState === 'DISABLED' ? 'Auto-posting paused' : 'Auto-posting resumed');
    } catch (e) {
      showToast(`Could not change auto-posting: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  }

  const dirty = selected && selected !== schedule;
  const paused = ruleState === 'DISABLED';
  const currentLabel = options.find(o => o.value === schedule)?.label || schedule;

  return (
    <Panel title="Auto-posting" action={ruleState ? <StatusPill status={paused ? 'paused' : 'active'} label={paused ? 'Paused' : 'Running'} /> : null}>
      <p className="ops-help" style={{ marginBottom: 14 }}>
        Controls how often the AI agent writes and publishes a new post to the Facebook Page. Changes take effect immediately.
      </p>
      {error ? (
        <Empty title="Could not load the schedule">
          {error}
          <div style={{ marginTop: 12 }}><button type="button" className="ops-btn ops-btn--sm" onClick={load}>Try again</button></div>
        </Empty>
      ) : schedule === null ? <Skeleton rows={2} /> : (
        <>
          {paused && (
            <div className="ops-note ops-note--review" style={{ marginBottom: 14 }}>
              Auto-posting is paused. The agent will not publish new posts until you resume it. You can still use Post now on the Overview page.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
            <div className="ops-field" style={{ minWidth: 200 }}>
              <label htmlFor="post-frequency">Post frequency</label>
              <select id="post-frequency" className="ops-select" value={selected} onChange={e => setSelected(e.target.value)} disabled={paused || saving}>
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <button type="button" className="ops-btn ops-btn--primary" onClick={handleSave} disabled={!dirty || saving || paused}>
              {saving && dirty ? 'Saving...' : 'Save'}
            </button>
            {!dirty && !paused && schedule && <span className="ops-help">Currently: <strong>{currentLabel}</strong></span>}
            <button type="button" className={`ops-btn${paused ? ' ops-btn--primary' : ''}`} onClick={handleTogglePause} disabled={saving} style={{ marginLeft: 'auto' }}>
              <Icon name={paused ? 'play' : 'pause'} /> {paused ? 'Resume posting' : 'Pause posting'}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

export default function SettingsPage() {
  const [showToast, toastNode] = useToast();

  return (
    <>
      <PageHeader title="Posting Settings" subtitle="How often the agent posts, how to pause it, and where the pieces live in AWS." />

      <PostSchedulePanel showToast={showToast} />

      <div className="ops-grid-2">
        <Panel title="Facebook token refresh">
          <p className="ops-help" style={{ marginBottom: 12, lineHeight: 1.6 }}>
            Facebook Page access tokens expire after about <strong>60 days</strong>. Run this in a VS Code terminal to store a fresh token in AWS Secrets Manager:
          </p>
          <pre className="ops-note mono" style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12.5, whiteSpace: 'pre-wrap', margin: 0 }}>
{`cd C:\\Users\\Homec\\hca-marketing-agent
.\\scripts\\set-token.ps1`}
          </pre>
          <p className="ops-help" style={{ marginTop: 10 }}>The README has the equivalent AWS CLI command.</p>
        </Panel>

        <Panel title="Agent configuration">
          <dl className="ops-dl">
            <div><dt>Environment</dt><dd>{ENV}</dd></div>
            <div><dt>AWS region</dt><dd>{REGION}</dd></div>
            <div><dt>Post schedule</dt><dd>Set in Auto-posting above</dd></div>
            <div><dt>Poll interval</dt><dd>Every 15 minutes</dd></div>
            <div><dt>Bedrock model</dt><dd>Claude Sonnet 4.6</dd></div>
          </dl>
        </Panel>

        <Panel title="AWS console links" flush>
          <ul className="ops-list">
            {CONSOLE_LINKS.map(l => (
              <li key={l.label}>
                <div className="ops-list__main">
                  <a href={l.url} target="_blank" rel="noreferrer" className="ops-cell-main ops-link">{l.label}</a>
                  <div className="ops-cell-sub">{l.sub}</div>
                </div>
                <Icon name="external" />
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Safety reminders">
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.9, color: 'var(--ink-2)' }}>
            <li>The agent posts <strong>organic, unpaid content only</strong>: no ads and no boosting.</li>
            <li>Nothing is ever deleted. Posts, comments, messages and reviews are permanent.</li>
            <li>Sensitive messages are <strong>escalated to your email</strong>, never auto-replied.</li>
            <li>Every action is recorded in the Audit Log.</li>
            <li>The Facebook token expires about 60 days after it is created. Refresh it before then.</li>
          </ul>
        </Panel>
      </div>
      {toastNode}
    </>
  );
}
